// Deterministic pseudo random numbers (mulberry32). The same seed gives the same
// sequence on the server and in the browser, so generated sample data never causes
// a hydration mismatch.

export type Random = {
    next: () => number
    int: (min: number, max: number) => number
    float: (min: number, max: number) => number
    chance: (probability: number) => boolean
    pick: <T>(items: readonly T[]) => T
    weighted: <T>(entries: readonly (readonly [T, number])[]) => T
    shuffle: <T>(items: readonly T[]) => T[]
}

export const createRandom = (seed: number): Random => {
    let state = seed >>> 0

    const next = (): number => {
        state = (state + 0x6d2b79f5) >>> 0
        let t = state
        t = Math.imul(t ^ (t >>> 15), t | 1)
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }

    const int = (min: number, max: number): number => min + Math.floor(next() * (max - min + 1))

    const float = (min: number, max: number): number => min + next() * (max - min)

    const chance = (probability: number): boolean => next() < probability

    const pick = <T>(items: readonly T[]): T => items[Math.floor(next() * items.length)]

    const weighted = <T>(entries: readonly (readonly [T, number])[]): T => {
        const total = entries.reduce((sum, [, weight]) => sum + weight, 0)
        let target = next() * total

        for (const [value, weight] of entries) {
            target -= weight
            if (target < 0) return value
        }

        return entries[entries.length - 1][0]
    }

    const shuffle = <T>(items: readonly T[]): T[] => {
        const copy = [...items]

        for (let index = copy.length - 1; index > 0; index--) {
            const swap = Math.floor(next() * (index + 1))
            const temp = copy[index]
            copy[index] = copy[swap]
            copy[swap] = temp
        }

        return copy
    }

    return { next, int, float, chance, pick, weighted, shuffle }
}

// FNV-1a hash, used to derive a stable seed from an id
export const hashString = (value: string): number => {
    let hash = 0x811c9dc5

    for (let index = 0; index < value.length; index++) {
        hash ^= value.charCodeAt(index)
        hash = Math.imul(hash, 0x01000193)
    }

    return hash >>> 0
}
